import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { pluralizeLocalWord } from '../../service/Pluralize/Pluralize';
import { TestBem } from '../../service/TestBem';
import { Banner } from '../../typings/domain';
import { BannerPreview } from '../BannerPreview/BannerPreview';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ContainerOverflow } from '../ContainerOverflow/ContainerOverflow';
import { Loader } from '../Loader/Loader';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { ScreenErrorMessage } from '../ScreenErrorMessage/ScreenErrorMessage';

import './BannerPreviewPopup.css';
import * as keyset from './BannerPreviewPopup.i18n';

export interface BannerPreviewPopupProps {
    anchor: React.ReactNode;
    isVisible: boolean;
    bannersTotalCount: number;
    banners?: Banner[];
    error?: string;
    onCancel: () => void;
    onClick: () => void;
    id: string;
}

const BLOCK = 'banner-preview-popup';

const i18n = i18nFactory(keyset);

export class BannerPreviewPopup extends React.Component<
    BannerPreviewPopupProps
> {
    render() {
        const { onCancel, anchor, isVisible, id } = this.props;

        return (
            <PopupDecorator
                maxBodyHeight={300}
                hasClose={true}
                onClose={onCancel}
                onOutsideClick={onCancel}
                anchor={anchor}
                visible={isVisible}
                cls={classNames(
                    Bem.cls(BLOCK, 'popup-preview'),
                    TestBem.cls(BLOCK, 'popup-preview', {
                        id
                    })
                )}
                directions={[
                    'left-center',
                    'right-center',
                    'bottom-center',
                    'top-center'
                ]}
                hasTail={true}
                target={'anchor'}
                header={this.getHeader()}
                footer={this.getFooter()}
            >
                {this.getBody()}
            </PopupDecorator>
        );
    }

    private getHeader() {
        const { banners, bannersTotalCount } = this.props;

        if (!banners) {
            return '';
        }

        if (banners.length === bannersTotalCount) {
            return i18n('banners');
        }

        return i18n('bannersPartial', {
            count: banners.length,
            totalCount: bannersTotalCount,
            banners: pluralizeLocalWord(
                [i18n('banners-1'), i18n('banners-2'), i18n('banners-5')],
                banners.length
            )
        });
    }

    private getBody() {
        return (
            <div className={Bem.cls(BLOCK, 'body')}>
                {this.getBodyContent()}
            </div>
        );
    }

    private getBodyContent() {
        const { banners, error } = this.props;

        if (error) {
            return (
                <ScreenErrorMessage
                    cls={Bem.cls(BLOCK, 'error-message')}
                    header={i18n('banners-loading-failed')}
                    text={error}
                />
            );
        }

        if (!banners) {
            return <Loader cls={Bem.cls(BLOCK, 'loader')} size={'l'} />;
        }

        if (!banners.length) {
            return (
                <ScreenErrorMessage
                    cls={Bem.cls(BLOCK, 'error-message')}
                    header={i18n('no-banners-available')}
                />
            );
        }

        return (
            <ContainerOverflow>
                {banners.map((banner, index) => {
                    const { id } = banner;

                    return (
                        <div
                            key={`${index}_${id}`}
                            className={Bem.cls(BLOCK, 'body-item')}
                        >
                            <BannerPreview
                                bannerPreviewData={banner}
                                column={true}
                            />
                        </div>
                    );
                })}
            </ContainerOverflow>
        );
    }

    private getFooter() {
        const { onClick, banners, id } = this.props;

        return (
            <div className={Bem.cls(BLOCK, 'footer')}>
                <ButtonDecorator
                    cls={TestBem.cls(BLOCK, 'button', { id })}
                    theme={'action'}
                    size={'n'}
                    onClick={onClick}
                    disabled={!banners || !banners.length}
                >
                    {i18n('go-to-banners')}
                </ButtonDecorator>
            </div>
        );
    }
}
